import {MaterialType, MeasurementType} from '@domain/material-storage'
import {dataTestId} from '@hconnect/uikit'
import {CheckOutlined} from '@mui/icons-material'
import {MenuItem, TextField} from '@mui/material'
import {useCallback, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {useConfirmDialog} from '@settings/modules/common/providers'
import {isDepartmentOrEquipmentNode} from '@settings/modules/plant-setup/helpers'
import {useSelectedItem} from '@settings/modules/plant-setup/providers'
import {useUrlParam} from '@settings/modules/routing'
import {useAddStorage} from '@settings/modules/storages/hooks'

export const useHandleEnableStorageForPlanning = () => {
  const {t} = useTranslation()
  const {openDialog} = useConfirmDialog()
  const plantCode = useUrlParam('plantCode')

  const {selectedItem} = useSelectedItem()

  const {mutate: addStorage, isPending: isAddingStorage} = useAddStorage(
    t('success.enabledStorageForPlanning', {name: selectedItem?.text ?? ''})
  )

  const storageTypeSelector = useRef<HTMLSelectElement>()

  const isEnableForPlanningAllowed = isDepartmentOrEquipmentNode(selectedItem)

  const handleEnableStorageForPlanning = useCallback(() => {
    if (!isEnableForPlanningAllowed) throw new Error('Enable for planning is not allowed')

    openDialog({
      title: t('plantSetup.enableForPlanningStorage'),
      description: t('plantSetup.enableForPlanningStorageDialog'),
      mainAction: {
        text: t('common.enable'),
        icon: <CheckOutlined />,
        onAction: () => {
          addStorage({
            plantCode,
            dto: {
              name: selectedItem.text ?? '',
              materialType: storageTypeSelector.current?.value as MaterialType,
              measurementType: MeasurementType.Auto,
              isOptimized: true,
              isMeasured: true,
              capacity: 0,
              deadStock: 0,
              minimumStockLevel: 0,
              minimumWeekendStockLevel: 0,
              materialId: null,
              upmId: selectedItem.upmId
            }
          })
        }
      },
      additionalContent: (
        <TextField
          inputRef={storageTypeSelector}
          variant="outlined"
          fullWidth
          select
          label={t('materialsSettings.materialType')}
          defaultValue={MaterialType.Cement}
          {...dataTestId('plant-structure-details-material-type-selector')}
        >
          {Object.values(MaterialType).map((type) => (
            <MenuItem
              key={type}
              value={type}
              {...dataTestId(`plant-structure-details-material-type-selector-${type}`)}
            >
              {t(`materialsSettings.materialTypes.${type}`)}
            </MenuItem>
          ))}
        </TextField>
      )
    })
  }, [
    isEnableForPlanningAllowed,
    selectedItem?.text,
    selectedItem?.upmId,
    plantCode,
    openDialog,
    t,
    addStorage
  ])

  return {
    isAddingStorage,
    handleEnableStorageForPlanning
  }
}
